import React, { useRef, useState } from "react";
import {
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Button,
} from "@mui/material";
import { Download, Share } from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const PrintData = ({
  children,
  fileName = "document.pdf",
  watermarkText = "This is an AI generated report.",
  showWatermark = true,
  watermarkColor = "#CCCCCC",
  watermarkFontSize = 20,
}) => {
  const contentRef = useRef(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const generatePDF = async () => {
    if (!contentRef.current) return null;

    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (element) =>
          element.classList.contains("exclude-from-pdf"), // Exclude elements with this class
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const contentWidth = canvas.width;
      const contentHeight = canvas.height;
      const aspectRatio = contentWidth / contentHeight;

      let pdfContentWidth = pageWidth - 20;
      let pdfContentHeight = pdfContentWidth / aspectRatio;

      if (pdfContentHeight > pageHeight - 20) {
        pdfContentHeight = pageHeight - 20;
        pdfContentWidth = pdfContentHeight * aspectRatio;
      }

      const contentX = (pageWidth - pdfContentWidth) / 2;
      const contentY = 20;

      if (showWatermark) {
        const watermarkX = pageWidth / 2;
        const watermarkY = 15;
        pdf.setFont("helvetica", "italic"); // Set font style to italic
        pdf.setFontSize(watermarkFontSize);
        pdf.setTextColor(watermarkColor);
        pdf.text(watermarkText, watermarkX, watermarkY, { align: "center" });
      }

      pdf.addImage(
        imgData,
        "PNG",
        contentX,
        contentY,
        pdfContentWidth,
        pdfContentHeight
      );

      return pdf;
    } catch (error) {
      console.error("Error generating PDF:", error);
      return null;
    }
  };

  const handleDownload = async () => {
    const pdf = await generatePDF();
    if (pdf) {
      pdf.save(fileName);
    }
  };

  const handleShare = async (method) => {
    const pdf = await generatePDF();
    if (pdf) {
      const blob = pdf.output("blob");
      const generatedPdfUrl = URL.createObjectURL(blob);
      setPdfUrl(generatedPdfUrl);

      switch (method) {
        case "native":
          try {
            await navigator.share({
              title: "PDF Document",
              text: `Check out this PDF document. You can view it here: ${generatedPdfUrl}`,
            });
          } catch (error) {
            console.error("Native sharing error:", error);
            setShareDialogOpen(true);
          }
          break;

        case "whatsapp":
          window.open(
            `https://wa.me/?text=${encodeURIComponent(
              `Check out this PDF: ${generatedPdfUrl}`
            )}`,
            "_blank"
          );
          break;

        case "email":
          const subject = encodeURIComponent("PDF Document");
          const body = encodeURIComponent(
            `Hi,\n\nPlease check out this PDF document at the following link:\n\n${generatedPdfUrl}\n\nYou can view it using the link above.`
          );

          const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
          const link = document.createElement("a");
          link.href = mailtoLink;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          break;
      }
    }
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  };

  return (
    <>
      {/* Content to capture */}
      <div
        ref={contentRef}
        style={{
          position: "relative",
          padding: "20px",
          backgroundColor: "#fff",
        }}
      >
        {/* Action Buttons at Top-Right */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            position: "absolute",
            top: "16px",
            right: "16px",
            zIndex: 10,
            backgroundColor: "#f5f5f5", // very light gray background
            borderBottom: "1px solid #ccc", // underline effect
            padding: "4px",
            borderRadius: "4px",
          }}
          className="exclude-from-pdf" // Exclude buttons from PDF
        >
          <Tooltip title="Download PDF">
            <IconButton
              color="primary"
              onClick={handleDownload}
              aria-label="Download PDF"
            >
              <Download />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share PDF">
            <IconButton
              color="secondary"
              onClick={() => handleShare("native")}
              aria-label="Share PDF"
            >
              <Share />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Actual Content */}
        {children}
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog}>
        <DialogTitle>Share PDF</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleShare("whatsapp")}
            >
              Share via WhatsApp
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleShare("email")}
            >
              Share via Email
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrintData;
